import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Tooltip,
  TablePagination,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where, orderBy, Timestamp, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

const WORKSHOP_STATUS = {
  WAITING: { value: 'waiting', label: 'Oczekuje', color: 'warning' },
  IN_PROGRESS: { value: 'in_progress', label: 'W realizacji', color: 'info' },
  COMPLETED: { value: 'completed', label: 'Ukończone', color: 'success' },
  CANCELLED: { value: 'cancelled', label: 'Anulowane', color: 'error' }
};

const REPAIR_TYPES = [
  { value: 'sizing', label: 'Zmiana rozmiaru' },
  { value: 'stone_replacement', label: 'Wymiana kamienia' },
  { value: 'cleaning', label: 'Czyszczenie' },
  { value: 'polishing', label: 'Polerowanie' },
  { value: 'repair', label: 'Naprawa' },
  { value: 'other', label: 'Inne' }
];

const Workshop = () => {
  const { currentUser } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const isAdmin = currentUser?.role === 'admin';

  const [items, setItems] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Stan dla wyszukiwania produktów
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Ref dla timera debounce
  const searchTimerRef = useRef(null);

  const [formData, setFormData] = useState({
    product_id: '',
    product_name: '',
    product_sku: '',
    repair_type: '',
    description: '',
    estimated_completion: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    cost: '',
    status: 'waiting',
    notes: ''
  });

  // Pobieranie elementów z pracowni
  const fetchWorkshopItems = useCallback(async () => {
    try {
      if (!currentUser) return;
      
      console.log('Pobieranie elementów z pracowni...');
      const workshopCollection = collection(db, 'workshop');
      let workshopQuery;
      
      if (isAdmin) {
        // Admin widzi wszystkie elementy
        workshopQuery = query(workshopCollection);
      } else {
        // Zwykli użytkownicy widzą tylko swoje elementy
        workshopQuery = query(workshopCollection, where('employee_id', '==', currentUser.uid));
      }
      
      const workshopSnapshot = await getDocs(workshopQuery);
      console.log('Pobrano elementy z pracowni, ilość:', workshopSnapshot.docs.length);
      
      const workshopItems = workshopSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          created_at: data.created_at instanceof Timestamp ? data.created_at.toDate() : new Date(),
          estimated_completion: data.estimated_completion instanceof Timestamp ? 
            data.estimated_completion.toDate() : new Date()
        };
      });
      
      setItems(workshopItems);
    } catch (error) {
      console.error('Błąd podczas pobierania elementów z pracowni:', error);
      enqueueSnackbar('Błąd podczas pobierania listy napraw', { variant: 'error' });
    }
  }, [enqueueSnackbar, isAdmin, currentUser]);
  
  // Generowanie nazwy produktu w zależności od kategorii
  const getProductDisplayName = useCallback((product) => {
    if (!product) return '';
    
    // Dla zegarków: marka + model + wielkość w mm
    if (product.category === 'watch') {
      const brand = product.brand || '';
      const model = product.model || '';
      const diameter = product.case_diameter ? `${product.case_diameter}mm` : '';
      return [brand, model, diameter].filter(Boolean).join(' ');
    } 
    
    // Dla biżuterii: jewelry_name lub inna dostępna nazwa
    return product.jewelry_name || '';
  }, []);
  
  // Wyszukiwanie produktów
  const searchProducts = useCallback(async (query) => {
    // Zawsze próbujemy wyszukiwać, nawet przy krótkich zapytaniach
    if (!query) return [];
    
    setSearchLoading(true);
    try {
      const productsCollection = collection(db, 'products');
      const searchQuery = query.toLowerCase();
      
      // Pobierz wszystkie produkty
      const productsSnapshot = await getDocs(productsCollection);
      console.log(`Pobrano ${productsSnapshot.docs.length} produktów do przeszukania`);
      
      // Mapuj i filtruj produkty
      const filteredProducts = productsSnapshot.docs
        .map(doc => {
          const data = doc.data();
          const product = {
            id: doc.id,
            ...data
          };
          
          // Generuj nazwę wyświetlania dla produktu
          const displayName = getProductDisplayName(product);
          
          return {
            ...product,
            // Dodajemy pole name jako alias dla wyświetlanej nazwy dla kompatybilności
            name: displayName,
            displayName: displayName
          };
        })
        .filter(product => {
          // Przeszukaj wszystkie pola tekstowe produktu
          const searchableFields = [
            product.jewelry_name,
            product.sku,
            product.id,
            product.model,
            product.brand,
            product.category,
            product.description,
            product.jewelry_type,
            product.material,
            product.gold_color,
            product.gold_purity,
            product.reference_number,
            product.serial_number,
            product.dial_color,
            product.custom_dial_color,
            product.movement_type,
            product.bracelet_type,
            String(product.case_diameter || '')
          ];
          
          // Sprawdź czy którekolwiek pole zawiera szukaną frazę
          return searchableFields.some(field => 
            field && typeof field === 'string' && field.toLowerCase().includes(searchQuery)
          );
        });
      
      console.log(`Znaleziono ${filteredProducts.length} pasujących produktów`);
      // Logowanie pierwszego produktu, aby sprawdzić strukturę
      if (filteredProducts.length > 0) {
        console.log('Przykładowy produkt:', {
          id: filteredProducts[0].id,
          displayName: filteredProducts[0].displayName,
          category: filteredProducts[0].category,
          sku: filteredProducts[0].sku
        });
      }
      return filteredProducts;
    } catch (error) {
      console.error('Błąd podczas wyszukiwania produktów:', error);
      return [];
    } finally {
      setSearchLoading(false);
    }
  }, [getProductDisplayName]);
  
  // Funkcja debounce dla wyszukiwania
  const debouncedSearch = (searchText) => {
    // Czyścimy poprzedni timer, jeśli istnieje
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    
    // Ustawiamy nowy timer
    searchTimerRef.current = setTimeout(async () => {
      if (searchText) {
        const results = await searchProducts(searchText);
        setProducts(results);
      } else {
        // Przy pustym zapytaniu pobieramy wszystkie produkty (z limitem)
        try {
          setSearchLoading(true);
          const productsCollection = collection(db, 'products');
          const productsSnapshot = await getDocs(query(productsCollection, limit(20)));
          
          const allProducts = productsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          setProducts(allProducts);
        } catch (error) {
          console.error('Błąd podczas pobierania produktów:', error);
        } finally {
          setSearchLoading(false);
        }
      }
    }, 500); // Opóźnienie 500ms
  };
  
  // Obsługa wyszukiwania produktów
  const handleProductSearch = (inputValue) => {
    setSearchQuery(inputValue);
    debouncedSearch(inputValue);
  };
  
  // Wybór produktu
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setFormData(prev => ({
      ...prev,
      product_id: product.id,
      product_name: product.displayName || product.name || '',
      product_sku: product.sku || ''
    }));
    
    console.log('Wybrano produkt:', {
      id: product.id,
      displayName: product.displayName,
      category: product.category,
      sku: product.sku
    });
  };

  useEffect(() => {
    if (currentUser) {
      fetchWorkshopItems();
    }
  }, [fetchWorkshopItems, currentUser]);

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setSelectedProduct(null);
    setFormData({
      product_id: '',
      product_name: '',
      product_sku: '',
      repair_type: '',
      description: '',
      estimated_completion: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      cost: '',
      status: 'waiting',
      notes: ''
    });
  };

  const handleAddItem = () => {
    setOpenDialog(true);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setFormData({
      product_id: item.product_id || '',
      product_name: item.product_name || '',
      product_sku: item.product_sku || '',
      repair_type: item.repair_type || '',
      description: item.description || '',
      estimated_completion: item.estimated_completion instanceof Date ? 
        format(item.estimated_completion, 'yyyy-MM-dd') : 
        format(new Date(), 'yyyy-MM-dd'),
      cost: item.cost || '',
      status: item.status || 'waiting',
      notes: item.notes || ''
    });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (!currentUser) {
        enqueueSnackbar('Musisz być zalogowany, aby wykonać tę operację', { variant: 'error' });
        return;
      }
      
      // Aktualizacja statusu produktu w bazie danych, jeśli wybrano produkt
      if (formData.product_id) {
        try {
          const productRef = doc(db, 'products', formData.product_id);
          await updateDoc(productRef, {
            status: 'in_workshop',
            workshop_status: formData.status,
            workshop_description: formData.description,
            last_updated: Timestamp.fromDate(new Date())
          });
          console.log('Zaktualizowano status produktu na "in_workshop"');
        } catch (error) {
          console.error('Błąd podczas aktualizacji statusu produktu:', error);
        }
      }
      
      const workshopData = {
        ...formData,
        employee_id: currentUser.uid,
        employee_name: `${currentUser.firstName} ${currentUser.lastName}`,
        created_at: Timestamp.fromDate(new Date()),
        estimated_completion: Timestamp.fromDate(new Date(formData.estimated_completion))
      };

      if (selectedItem) {
        // Aktualizacja istniejącego elementu
        const workshopRef = doc(db, 'workshop', selectedItem.id);
        await updateDoc(workshopRef, {
          ...workshopData,
          last_updated: Timestamp.fromDate(new Date())
        });
        enqueueSnackbar('Naprawa została zaktualizowana', { variant: 'success' });
      } else {
        // Dodanie nowego elementu
        await addDoc(collection(db, 'workshop'), workshopData);
        enqueueSnackbar('Naprawa została dodana', { variant: 'success' });
      }

      handleDialogClose();
      fetchWorkshopItems();
    } catch (error) {
      console.error('Błąd podczas zapisywania naprawy:', error);
      enqueueSnackbar('Błąd podczas zapisywania naprawy', { variant: 'error' });
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Czy na pewno chcesz usunąć tę naprawę?')) {
      try {
        // Pobierz dane elementu przed usunięciem
        const workshopRef = doc(db, 'workshop', itemId);
        const workshopSnap = await getDocs(query(collection(db, 'workshop'), where('__name__', '==', itemId)));
        
        if (!workshopSnap.empty) {
          const workshopData = workshopSnap.docs[0].data();
          
          // Jeśli element ma powiązany produkt, zaktualizuj jego status
          if (workshopData && workshopData.product_id) {
            try {
              const productRef = doc(db, 'products', workshopData.product_id);
              await updateDoc(productRef, {
                status: 'available',
                workshop_status: null,
                workshop_description: null,
                last_updated: Timestamp.fromDate(new Date())
              });
              console.log('Zaktualizowano status produktu na "available"');
            } catch (productError) {
              console.error('Błąd podczas aktualizacji statusu produktu:', productError);
            }
          }
        }
        
        // Usuń element z pracowni
        await deleteDoc(doc(db, 'workshop', itemId));
        enqueueSnackbar('Naprawa została usunięta', { variant: 'success' });
        fetchWorkshopItems();
      } catch (error) {
        console.error('Błąd podczas usuwania naprawy:', error);
        enqueueSnackbar('Błąd podczas usuwania naprawy', { variant: 'error' });
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontFamily: 'Cinzel, serif' }}>
          Towar do pracowni
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
        >
          Dodaj naprawę
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nazwa produktu</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Typ naprawy</TableCell>
              <TableCell>Opis</TableCell>
              <TableCell>Planowana data realizacji</TableCell>
              <TableCell>Koszt</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Pracownik</TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.product_name}</TableCell>
                  <TableCell>{item.product_sku || '-'}</TableCell>
                  <TableCell>
                    {REPAIR_TYPES.find(type => type.value === item.repair_type)?.label || item.repair_type || '-'}
                  </TableCell>
                  <TableCell>{item.description || '-'}</TableCell>
                  <TableCell>
                    {item.estimated_completion instanceof Date ? 
                      format(item.estimated_completion, 'dd.MM.yyyy', { locale: pl }) : 
                      '-'}
                  </TableCell>
                  <TableCell>{item.cost ? `${item.cost} zł` : '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={WORKSHOP_STATUS[item.status]?.label || item.status || 'Nieznany'}
                      color={WORKSHOP_STATUS[item.status]?.color || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{item.employee_name || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title="Edytuj">
                      <IconButton size="small" onClick={() => handleEditItem(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Usuń">
                      <IconButton size="small" onClick={() => handleDeleteItem(item.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={items.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedItem ? 'Edytuj naprawę' : 'Dodaj nową naprawę'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
                <Autocomplete
                fullWidth
                options={products}
                loading={searchLoading}
                getOptionLabel={(option) => `${option.displayName || option.name || ''} ${option.sku ? `(${option.sku})` : ''}`}
                filterOptions={(x) => x}
                onInputChange={(event, newInputValue) => handleProductSearch(newInputValue)}
                onChange={(event, newValue) => {
                  if (newValue) handleProductSelect(newValue);
                }}
                noOptionsText="Brak pasujących produktów"
                loadingText="Wyszukiwanie produktów..."
                renderOption={(props, option) => {
                  // Usuwamy klucz z props i przekazujemy go bezpośrednio do elementu li
                  const { key, ...otherProps } = props;
                  return (
                    <li key={option.id || key} {...otherProps}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {option.displayName || option.name || 'Bez nazwy'}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', width: '100%' }}>
                          {option.sku && (
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                              SKU: {option.sku}
                            </Typography>
                          )}
                          {option.category === 'watch' ? (
                            <>
                              {option.reference_number && (
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                                  Ref: {option.reference_number}
                                </Typography>
                              )}
                              {option.dial_color && (
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                                  Tarcza: {option.dial_color === 'other' && option.custom_dial_color ? 
                                    option.custom_dial_color : option.dial_color}
                                </Typography>
                              )}
                              {option.bracelet_type && (
                                <Typography variant="body2" color="text.secondary">
                                  Bransoleta: {option.bracelet_type}
                                </Typography>
                              )}
                            </>
                          ) : (
                            <>
                              {option.material && (
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                                  Materiał: {option.material}
                                </Typography>
                              )}
                              {option.category && (
                                <Typography variant="body2" color="text.secondary">
                                  Kategoria: {option.category}
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Wyszukaj produkt (nazwa, SKU, model, kategoria)"
                    placeholder="Zacznij pisać, aby wyszukać produkt..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                      startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nazwa produktu"
                value={formData.product_name}
                onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SKU produktu"
                value={formData.product_sku}
                onChange={(e) => setFormData({ ...formData, product_sku: e.target.value })}
                disabled={!!selectedProduct}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Typ naprawy</InputLabel>
                <Select
                  value={formData.repair_type}
                  onChange={(e) => setFormData({ ...formData, repair_type: e.target.value })}
                  label="Typ naprawy"
                >
                  {REPAIR_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Opis naprawy"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Planowana data realizacji"
                value={formData.estimated_completion}
                onChange={(e) => setFormData({ ...formData, estimated_completion: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Koszt (zł)"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  label="Status"
                >
                  {Object.values(WORKSHOP_STATUS).map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Uwagi"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} startIcon={<ClearIcon />}>
            Anuluj
          </Button>
          <Button onClick={handleSubmit} variant="contained" startIcon={<CheckIcon />}>
            {selectedItem ? 'Zapisz zmiany' : 'Dodaj naprawę'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Workshop;
